import { formatTimestamp } from "../lib/time";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import {
  Loader2, RefreshCw, Save, Server, Zap, Volume2, Cpu,
  Play, Pause, Trash2, Link as LinkIcon, Terminal,
  ChevronLeft, AlertCircle, AlertTriangle, Database, Rocket,
} from 'lucide-react';

import { Button } from '../components_dashboard/ui/button';
import { Input } from '../components_dashboard/ui/input';
import { Label } from '../components_dashboard/ui/label';
import { toast } from '../components/CustomToast';
import { useAuth } from '../context/AuthContext';

const apiUrl = process.env.REACT_APP_BACKEND_URL;
const BASE = `${apiUrl}/api/v1/admin/gpu`;
const GPUS = ['H100', 'A100-80GB', 'A100', 'H200', 'L4'];
const LOG_POLL_MS = 1200;

// ─── Helpers ──────────────────────────────────────────────────────────────────
function fmtCost(it) {
  const c = it?.session_cost ?? it?.storage_cost ?? it?.cost ?? it?.current_cost;
  if (c == null) return '—';
  return (typeof c === 'number' ? c.toFixed(2) : c);
}
function fmtUptime(it, running) {
  if (!running) return '0';
  const raw = it?.runtime || it?.uptime || it?.elapsed;
  if (!raw) return '—';
  if (typeof raw === 'string') {
    const m = raw.match(/(\d+)\s*day/i);
    const h = raw.match(/(\d+)\s*hour/i);
    const mi = raw.match(/(\d+)\s*minute/i);
    const parts = [];
    if (m) parts.push(m[1] + 'd');
    if (h) parts.push(h[1] + 'h');
    if (mi) parts.push(mi[1] + 'm');
    return parts.length ? parts.join(' ') : raw;
  }
  return String(raw);
}
function statusBadge(status) {
  const s = (status || '').toLowerCase();
  if (/run/.test(s)) return { cls: 'bg-emerald-50 text-emerald-700 ring-emerald-200', label: 'Running' };
  if (/paus/.test(s)) return { cls: 'bg-amber-50 text-amber-700 ring-amber-200', label: 'Paused' };
  return { cls: 'bg-slate-100 text-slate-600 ring-slate-200', label: status || '—' };
}
function colorize(line) {
  if (/error|traceback|failed|exception/i.test(line)) return 'text-rose-400';
  if (/startup complete|uvicorn running|live|🟢/i.test(line)) return 'text-emerald-400';
  if (/warning|deprecated/i.test(line)) return 'text-amber-400';
  if (/\[setup\]/i.test(line)) return 'text-violet-300';
  if (/INFO/i.test(line)) return 'text-sky-300';
  return 'text-slate-300';
}

// ─── Page ─────────────────────────────────────────────────────────────────────
export default function DeployGpuDashboard() {
  const { token, user } = useAuth();
  const auth = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  // High-level view: 'instances' (table) | 'llm' (launcher) | 'tts' (launcher) | 'attached' (logs viewer)
  const [view, setView] = useState('instances');
  const [activeTab, setActiveTab] = useState('instances');  // which nav tab is highlighted
  const [launcherOnline, setLauncherOnline] = useState(null);

  // Instances
  const [instances, setInstances] = useState([]);
  const [loadingInstances, setLoadingInstances] = useState(false);

  // Picker: list of bundles per category (no file contents — only metadata)
  const [bundlesLlm, setBundlesLlm] = useState(null);   // null = not loaded yet
  const [bundlesTts, setBundlesTts] = useState(null);
  // Which bundle _id the user picked for each tab (null → show picker, set → show editor)
  const [selBundleIdLlm, setSelBundleIdLlm] = useState(null);
  const [selBundleIdTts, setSelBundleIdTts] = useState(null);

  // Bundle editor state per category (loaded after picker selection)
  const [bundleLlm, setBundleLlm] = useState(null);
  const [bundleTts, setBundleTts] = useState(null);
  const [activeFileLlm, setActiveFileLlm] = useState(0);
  const [activeFileTts, setActiveFileTts] = useState(0);
  const [dirtyLlm, setDirtyLlm] = useState(false);
  const [dirtyTts, setDirtyTts] = useState(false);
  const [savingBundle, setSavingBundle] = useState(false);

  // Launch form per mode
  const [formLlm, setFormLlm] = useState({ name: 'qwen3-server', gpu: 'H100', storage: 100 });
  const [formTts, setFormTts] = useState({ name: 'orpheus-tts', gpu: 'A100', storage: 100 });

  // Live logs (polled)
  const [mainLog, setMainLog] = useState([]);
  const [apiLog, setApiLog] = useState([]);
  const [logTab, setLogTab] = useState('main');
  const mainSinceRef = useRef(0);
  const apiSinceRef  = useRef(0);
  const pollRef = useRef(null);

  // Attached logs target (used in 'attached' view)
  const [attachedInstance, setAttachedInstance] = useState(null);

  // Custom confirm dialog (replaces window.confirm)
  const [confirmDlg, setConfirmDlg] = useState({ open: false });
  const confirm = useCallback((opts) => new Promise((resolve) => {
    setConfirmDlg({
      open: true,
      title: opts?.title || 'Are you sure?',
      message: opts?.message || '',
      confirmLabel: opts?.confirmLabel || 'Confirm',
      cancelLabel: opts?.cancelLabel || 'Cancel',
      danger: !!opts?.danger,
      onResolve: (v) => { setConfirmDlg({ open: false }); resolve(v); },
    });
  }), []);

  // ─── Health probe + initial load ───────────────────────────────────────────
  const probeLauncher = useCallback(async () => {
    try {
      const r = await axios.get(`${BASE}/health`, auth);
      setLauncherOnline(Boolean(r.data?.ok));
    } catch {
      setLauncherOnline(false);
    }
  }, [auth]);

  const fetchInstances = useCallback(async () => {
    if (!token) return;
    setLoadingInstances(true);
    try {
      const r = await axios.get(`${BASE}/instances`, auth);
      setInstances(Array.isArray(r.data?.instances) ? r.data.instances : []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load instances.');
    } finally {
      setLoadingInstances(false);
    }
  }, [token, auth]);

  // Fetch ONE bundle by its _id. The bundle's own `type` field tells us which
  // category (llm or tts) editor state to slot it into.
  const fetchBundle = useCallback(async (bundleId) => {
    try {
      const r = await axios.get(`${BASE}/bundle/${bundleId}`, auth);
      const d = r.data;
      if (d?.type === 'llm') { setBundleLlm(d); setActiveFileLlm(0); setDirtyLlm(false); }
      else if (d?.type === 'tts') { setBundleTts(d); setActiveFileTts(0); setDirtyTts(false); }
      else toast.error(`Bundle ${bundleId} has unexpected type '${d?.type}'`);
    } catch (e) {
      toast.error(e.response?.data?.detail || `Failed to load bundle ${bundleId}.`);
    }
  }, [auth]);

  // Fetch the LIST of bundles for one category (used by the picker)
  const fetchBundleList = useCallback(async (category) => {
    try {
      const r = await axios.get(`${BASE}/bundles`, { ...auth, params: { category } });
      const bundles = r.data?.bundles || [];
      if (category === 'llm') setBundlesLlm(bundles);
      else                    setBundlesTts(bundles);
      return bundles;
    } catch (e) {
      toast.error(e.response?.data?.detail || `Failed to load ${category} bundles.`);
      return [];
    }
  }, [auth]);

  useEffect(() => { probeLauncher(); fetchInstances(); }, [probeLauncher, fetchInstances]);

  // ─── Log polling (active in launch/attached views) ─────────────────────────
  const stopPolling = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  }, []);

  const startPolling = useCallback((includeApi) => {
    stopPolling();
    setMainLog([]); setApiLog([]);
    mainSinceRef.current = 0; apiSinceRef.current = 0;
    const tick = async () => {
      try {
        const r = await axios.get(`${BASE}/logs`, { ...auth, params: { since: mainSinceRef.current } });
        const lines = r.data?.lines || [];
        if (lines.length) {
          setMainLog((prev) => [...prev, ...lines]);
          mainSinceRef.current = r.data?.total ?? mainSinceRef.current + lines.length;
        }
      } catch {}
      if (includeApi) {
        try {
          const r = await axios.get(`${BASE}/logs/api`, { ...auth, params: { since: apiSinceRef.current } });
          const lines = r.data?.lines || [];
          if (lines.length) {
            setApiLog((prev) => [...prev, ...lines]);
            apiSinceRef.current = r.data?.total ?? apiSinceRef.current + lines.length;
          }
        } catch {}
      }
    };
    pollRef.current = setInterval(tick, LOG_POLL_MS);
    tick();
  }, [auth, stopPolling]);

  useEffect(() => () => stopPolling(), [stopPolling]);

  // ─── Navigation ────────────────────────────────────────────────────────────
  const goInstances = () => {
    stopPolling();
    setView('instances'); setActiveTab('instances');
    fetchInstances();
  };
  // `category` here is 'llm' or 'tts' — the tab the user clicked.
  // Always show the picker first so the user can choose between multiple
  // bundles of that category (e.g. Production vs Staging). Auto-pick if there's
  // only one bundle in the category.
  const goLaunch = async (category) => {
    stopPolling();
    setView(category); setActiveTab(category);
    if (category === 'llm') setSelBundleIdLlm(null);
    else                    setSelBundleIdTts(null);
    const list = await fetchBundleList(category);
    if (list.length === 1) {
      const onlyId = list[0]._id;
      if (category === 'llm') setSelBundleIdLlm(onlyId);
      else                    setSelBundleIdTts(onlyId);
      fetchBundle(onlyId);
    }
  };
  const pickBundle = (category, bundleId) => {
    if (category === 'llm') setSelBundleIdLlm(bundleId);
    else                    setSelBundleIdTts(bundleId);
    fetchBundle(bundleId);
  };
  // "Change bundle" button — return to picker without leaving the tab
  const clearSelection = (category) => {
    if (category === 'llm') { setSelBundleIdLlm(null); setBundleLlm(null); setDirtyLlm(false); }
    else                    { setSelBundleIdTts(null); setBundleTts(null); setDirtyTts(false); }
    fetchBundleList(category);
  };

  // ─── Instance actions ─────────────────────────────────────────────────────
  const instanceAction = async (mid, action, confirmOpts) => {
    if (confirmOpts) {
      const ok = await confirm(confirmOpts);
      if (!ok) return;
    }
    try {
      await axios.post(`${BASE}/instance/${mid}/${action}`, {}, auth);
      toast.success(`${action.toUpperCase()} requested`);
      setTimeout(fetchInstances, 1500);
    } catch (e) {
      toast.error(e.response?.data?.detail || `${action} failed`);
    }
  };
  const copyApi = async (mid) => {
    toast.info('Probing endpoints…');
    try {
      const r = await axios.get(`${BASE}/instance/${mid}/api_url`, auth);
      const url = r.data?.working || (r.data?.candidates && r.data.candidates[0]);
      if (!url) return toast.error('No API URL');
      await navigator.clipboard.writeText(url);
      toast.success(r.data?.working ? 'Live URL copied' : 'URL copied (unverified)');
    } catch (e) {
      toast.error('Probe failed');
    }
  };
  const copySsh = async (mid) => {
    try {
      const r = await axios.get(`${BASE}/instance/${mid}`, auth);
      const cmd = r.data?.ssh_str || r.data?.ssh_command || r.data?.ssh || `jl ssh ${mid}`;
      await navigator.clipboard.writeText(cmd);
      toast.success('SSH command copied');
    } catch {
      toast.error('SSH lookup failed');
    }
  };
  const attachLogs = async (instance) => {
    try {
      await axios.post(`${BASE}/instance/${instance.machine_id || instance.id}/attach_logs`, {}, auth);
      setAttachedInstance(instance);
      setView('attached');
      // Keep "Instances" highlighted in nav (per existing UX)
      startPolling(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Attach failed');
    }
  };

  // ─── Launch / Stop ─────────────────────────────────────────────────────────
  // bundleId is the gpu_deploy doc _id (e.g. "llm-492266" or "llm-68101b").
  // Backend reads the bundle's `type` to know LLM vs TTS log behavior.
  const doLaunch = async (bundleId) => {
    const isLlm = bundleId === selBundleIdLlm;
    const f = isLlm ? formLlm : formTts;
    try {
      await axios.post(`${BASE}/launch`, { bundle_id: bundleId, ...f }, auth);
      toast.success(`Launching ${bundleId} — opening Instances`);
      goInstances();
      [3000, 8000, 15000, 25000, 45000, 75000].forEach((ms) => setTimeout(fetchInstances, ms));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Launch failed');
    }
  };

  // ─── Bundle editor ─────────────────────────────────────────────────────────
  // saveBundle takes the bundle's _id (e.g. "llm-492266"). Production and Staging
  // are separate docs with their own _id, so they save independently.
  const saveBundle = async (bundleId) => {
    const isLlm = bundleId === selBundleIdLlm;
    const b = isLlm ? bundleLlm : bundleTts;
    if (!b) return;
    setSavingBundle(true);
    try {
      const files = b.files.map((f) => ({ name: f.name, content: f.content || '' }));
      const r = await axios.post(`${BASE}/bundle/${bundleId}`, { files }, auth);
      toast.success(`Saved ${r.data?.saved ?? files.length} file(s)`);
      if (isLlm) setDirtyLlm(false); else setDirtyTts(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSavingBundle(false);
    }
  };
  const updateBundleContent = (mode, content) => {
    if (mode === 'llm') {
      const idx = activeFileLlm; if (!bundleLlm) return;
      const next = { ...bundleLlm, files: bundleLlm.files.map((f, i) => i === idx ? { ...f, content } : f) };
      setBundleLlm(next); setDirtyLlm(true);
    } else {
      const idx = activeFileTts; if (!bundleTts) return;
      const next = { ...bundleTts, files: bundleTts.files.map((f, i) => i === idx ? { ...f, content } : f) };
      setBundleTts(next); setDirtyTts(true);
    }
  };

  // ────────────────────────────────────────────────────────────────────────
  // RENDER
  // ────────────────────────────────────────────────────────────────────────
  return (
    <div className="flex-1 flex flex-col p-6">
      
      <main className="flex-1 min-h-0 p-6 overflow-y-auto  lg:p-8">
        <div className="mx-auto max-w-7xl space-y-6">

          {/* Page header */}
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-violet-100 bg-violet-50 px-3 py-1 text-xs font-semibold text-violet-700">
                <Server className="w-3.5 h-3.5" />
                Deploy GPU
              </div>
              <h1 className="mt-3 text-3xl font-bold tracking-tight text-slate-900">GPU Deployments</h1>
              <p className="mt-1 text-slate-500">Spin up Qwen LLM and Orpheus TTS instances on Jarvis Labs GPUs.</p>
            </div>

            <div className="flex items-center gap-2">
              <TabButton active={activeTab === 'instances'} icon={<Database className="w-4 h-4 mr-2" />} label="Instances" onClick={goInstances} />
              <TabButton active={activeTab === 'llm'}       icon={<Zap className="w-4 h-4 mr-2" />}      label="Launch LLM" onClick={() => goLaunch('llm')} />
              <TabButton active={activeTab === 'tts'}       icon={<Volume2 className="w-4 h-4 mr-2" />}  label="Launch TTS" onClick={() => goLaunch('tts')} accent="purple" />
            </div>
          </div>

          {/* Launcher offline banner */}
          {launcherOnline === false && (
            <div className="flex items-center gap-3 p-4 border rounded-xl border-amber-200 bg-amber-50 text-amber-800">
              <AlertCircle className="w-5 h-5 shrink-0" />
              <div className="text-sm">
                <strong>GPU launcher offline.</strong> The proxy can't reach the Jarvis launcher service. Start it
                with <code className="px-1 py-0.5 rounded bg-amber-100">python3 jarvis/jarvis1work.py</code> on the
                backend host, or set <code className="px-1 py-0.5 rounded bg-amber-100">GPU_LAUNCHER_URL</code>.
              </div>
              <Button size="sm" variant="ghost" onClick={probeLauncher} className="ml-auto">Retry</Button>
            </div>
          )}

          {/* ── VIEW: ATTACHED LOGS ─────────────────────────────────────────── */}
          {view === 'attached' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-white border rounded-xl border-violet-100">
                <div className="flex items-center gap-3">
                  <button type="button" onClick={goInstances}
                    className="inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium text-white shadow-sm transition-all bg-gradient-to-r from-violet-600 to-purple-700 hover:from-violet-700 hover:to-purple-800">
                    <ChevronLeft className="w-4 h-4 mr-1" />Back to Instances
                  </button>
                  <div className="text-sm text-slate-600">
                    Viewing logs from <strong className="text-violet-700">{attachedInstance?.name}</strong>
                    <span className="ml-2 text-xs text-slate-400">ID {attachedInstance?.machine_id || attachedInstance?.id}</span>
                  </div>
                </div>
              </div>
              <TerminalPanel
                logTab={logTab} setLogTab={setLogTab}
                mainLog={mainLog} apiLog={apiLog}
                dual onClear={() => { setMainLog([]); setApiLog([]); }}
              />
            </div>
          )}

          {/* ── VIEW: INSTANCES ─────────────────────────────────────────────── */}
          {view === 'instances' && (
            <InstancesPanel
              instances={instances}
              loading={loadingInstances}
              onRefresh={fetchInstances}
              onAction={instanceAction}
              onCopyApi={copyApi}
              onCopySsh={copySsh}
              onAttach={attachLogs}
            />
          )}

          {/* ── VIEW: LLM ─ picker first, launcher once a bundle is selected ── */}
          {view === 'llm' && !selBundleIdLlm && (
            <BundlePicker
              category="llm"
              bundles={bundlesLlm}
              onPick={(id) => pickBundle('llm', id)}
              onRefresh={() => fetchBundleList('llm')}
              accent="violet"
            />
          )}
          {view === 'llm' && selBundleIdLlm && (
            <LauncherView
              mode="llm"
              bundleId={selBundleIdLlm}
              bundle={bundleLlm}
              activeFile={activeFileLlm}
              setActiveFile={setActiveFileLlm}
              dirty={dirtyLlm}
              onContentChange={(c) => updateBundleContent('llm', c)}
              onSave={() => saveBundle(selBundleIdLlm)}
              onReload={() => fetchBundle(selBundleIdLlm)}
              onChangeBundle={() => clearSelection('llm')}
              saving={savingBundle}
              form={formLlm}
              setForm={setFormLlm}
              onLaunch={() => doLaunch(selBundleIdLlm)}
              modelLabel="Qwen3.6-35B-FP8"
              accentClass="from-violet-600 to-purple-700 hover:from-violet-700 hover:to-purple-800"
            />
          )}

          {/* ── VIEW: TTS LAUNCHER ──────────────────────────────────────────── */}
          {/* ── VIEW: TTS ─ picker first, launcher once a bundle is selected ── */}
          {view === 'tts' && !selBundleIdTts && (
            <BundlePicker
              category="tts"
              bundles={bundlesTts}
              onPick={(id) => pickBundle('tts', id)}
              onRefresh={() => fetchBundleList('tts')}
              accent="fuchsia"
            />
          )}
          {view === 'tts' && selBundleIdTts && (
            <LauncherView
              mode="tts"
              bundleId={selBundleIdTts}
              bundle={bundleTts}
              activeFile={activeFileTts}
              setActiveFile={setActiveFileTts}
              dirty={dirtyTts}
              onContentChange={(c) => updateBundleContent('tts', c)}
              onSave={() => saveBundle(selBundleIdTts)}
              onReload={() => fetchBundle(selBundleIdTts)}
              onChangeBundle={() => clearSelection('tts')}
              saving={savingBundle}
              form={formTts}
              setForm={setFormTts}
              onLaunch={() => doLaunch(selBundleIdTts)}
              modelLabel="Orpheus-3B Hindi TTS"
              accentClass="from-fuchsia-600 to-purple-700 hover:from-fuchsia-700 hover:to-purple-800"
            />
          )}
        </div>
      </main>

      {/* Custom confirm dialog (replaces native window.confirm — matches dashboard theme) */}
      <ConfirmDialog dlg={confirmDlg} />
    </div>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────
function ConfirmDialog({ dlg }) {
  // Keyboard: Esc cancels, Enter confirms
  useEffect(() => {
    if (!dlg.open) return undefined;
    const handler = (e) => {
      if (e.key === 'Escape') dlg.onResolve(false);
      if (e.key === 'Enter')  dlg.onResolve(true);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [dlg]);

  if (!dlg.open) return null;
  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm"
      onClick={() => dlg.onResolve(false)}
    >
      <div
        className="w-full max-w-md p-6 bg-white border shadow-2xl rounded-2xl border-slate-200"
        onClick={(e) => e.stopPropagation()}
        role="alertdialog" aria-modal="true"
      >
        <div className="flex items-start gap-4">
          <div className={`flex items-center justify-center w-11 h-11 rounded-full shrink-0
            ${dlg.danger ? 'bg-rose-100 text-rose-600' : 'bg-violet-100 text-violet-600'}`}>
            <AlertTriangle className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-base font-semibold text-slate-900">{dlg.title}</div>
            {dlg.message && (
              <div className="mt-1.5 text-sm leading-relaxed text-slate-600">{dlg.message}</div>
            )}
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <Button
            variant="outline"
            onClick={() => dlg.onResolve(false)}
            className="border-slate-200 text-slate-700 hover:bg-slate-100"
          >
            {dlg.cancelLabel}
          </Button>
          <Button
            onClick={() => dlg.onResolve(true)}
            autoFocus
            className={dlg.danger
              ? 'bg-rose-600 hover:bg-rose-700 text-white'
              : 'bg-gradient-to-r from-violet-600 to-purple-700 hover:from-violet-700 hover:to-purple-800 text-white'}
          >
            {dlg.confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}

function TabButton({ active, icon, label, onClick, accent }) {
  const activeCls = accent === 'purple'
    ? 'bg-gradient-to-r from-fuchsia-600 to-purple-700 text-white shadow-sm'
    : 'bg-gradient-to-r from-violet-600 to-purple-700 text-white shadow-sm';
  return (
    <button onClick={onClick}
      className={`inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-all
        ${active ? activeCls : 'bg-white border border-slate-200 text-slate-600 hover:text-violet-700 hover:border-violet-200'}`}>
      {icon}{label}
    </button>
  );
}

function InstancesPanel({ instances, loading, onRefresh, onAction, onCopyApi, onCopySsh, onAttach }) {
  return (
    <div className="bg-white border rounded-2xl border-slate-200 shadow-sm">
      <div className="flex items-center justify-between p-5 border-b border-slate-100">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">All Instances</h2>
          <p className="mt-0.5 text-sm text-slate-500">Your Jarvis Labs GPU instances</p>
        </div>
        <Button variant="outline" onClick={onRefresh} disabled={loading} className="border-slate-200 text-slate-700 hover:bg-slate-100">
          {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
          Refresh
        </Button>
      </div>

      {/* Show the spinner placeholder ONLY on the very first load (no data yet).
          Subsequent refreshes keep the existing table visible — the Refresh button
          icon spins so the user still gets feedback that an update is in flight. */}
      {loading && instances.length === 0 ? (
        <div className="flex items-center justify-center gap-2 px-4 py-16 text-sm text-slate-500">
          <Loader2 className="w-4 h-4 animate-spin" />Loading instances…
        </div>
      ) : instances.length === 0 ? (
        <div className="px-4 py-16 text-sm text-center text-slate-500">
          No instances yet. Click <strong>Launch LLM</strong> or <strong>Launch TTS</strong> above to spin one up.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs tracking-wide text-left uppercase text-slate-500 border-b border-slate-100">
              <tr>
                <th className="px-5 py-3 font-semibold">Name</th>
                <th className="px-5 py-3 font-semibold">Device</th>
                <th className="px-5 py-3 font-semibold">Region</th>
                <th className="px-5 py-3 font-semibold">Status</th>
                <th className="px-5 py-3 font-semibold">Cost</th>
                <th className="px-5 py-3 font-semibold">Uptime</th>
                <th className="px-5 py-3 font-semibold">Access</th>
                <th className="px-5 py-3 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {instances.map((it) => {
                const mid = it.machine_id || it.id;
                const status = (it.status || '').toString();
                const running = /run/i.test(status);
                const paused  = /paus/i.test(status);
                const badge   = statusBadge(status);
                return (
                  <tr key={mid} className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50/60">
                    <td className="px-5 py-4">
                      <div className="font-semibold text-slate-900">{it.name || '—'}</div>
                      <div className="mt-0.5 text-xs text-slate-400">{mid}</div>
                    </td>
                    <td className="px-5 py-4 text-slate-700">{(it.num_gpus || 1)} × {it.gpu_type || it.gpu || '?'}</td>
                    <td className="px-5 py-4">
                      <span className="inline-flex items-center px-2 py-1 text-xs font-semibold text-sky-700 bg-sky-50 ring-1 ring-sky-200 rounded">
                        {it.region || '—'}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      <span className={`inline-flex items-center px-2.5 py-1 text-xs font-semibold rounded-full ring-1 ${badge.cls}`}>
                        {badge.label}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-slate-700">{fmtCost(it)}</td>
                    <td className="px-5 py-4 text-slate-700">{fmtUptime(it, running)}</td>
                    <td className="px-5 py-4">
                      <div className="flex flex-wrap gap-1.5">
                        <AccessBtn onClick={() => onCopyApi(mid)} disabled={!running} icon={<LinkIcon className="w-3 h-3" />}>API</AccessBtn>
                        <AccessBtn onClick={() => onCopySsh(mid)} disabled={!running} icon={<Terminal className="w-3 h-3" />}>SSH</AccessBtn>
                        <AccessBtn onClick={() => onAttach(it)}   disabled={!running} icon={<Cpu className="w-3 h-3" />}>Logs</AccessBtn>
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center justify-end gap-1.5">
                        {running && (
                          <IconBtn title="Pause" onClick={() => onAction(mid, 'pause', {
                            title: `Pause ${it.name}?`,
                            message: 'Compute billing stops; a small storage cost continues. You can resume the same instance later from this page.',
                            confirmLabel: 'Pause Instance',
                          })}>
                            <Pause className="w-4 h-4" />
                          </IconBtn>
                        )}
                        {paused && (
                          <IconBtn title="Resume" onClick={() => onAction(mid, 'resume')}>
                            <Play className="w-4 h-4" />
                          </IconBtn>
                        )}
                        <IconBtn danger title="Destroy" onClick={() => onAction(mid, 'destroy', {
                          title: `Destroy ${it.name}?`,
                          message: 'This is irreversible. All data on the instance is lost. If you need to keep data across instances, attach a filesystem instead.',
                          confirmLabel: 'Destroy Permanently',
                          danger: true,
                        })}>
                          <Trash2 className="w-4 h-4" />
                        </IconBtn>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function AccessBtn({ onClick, disabled, icon, children }) {
  return (
    <button onClick={onClick} disabled={disabled}
      className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded ring-1 transition
        ${disabled
          ? 'text-slate-400 bg-slate-50 ring-slate-200 cursor-not-allowed'
          : 'text-violet-700 bg-violet-50 ring-violet-200 hover:bg-violet-100'}`}>
      {icon}{children}
    </button>
  );
}
function IconBtn({ children, onClick, title, danger }) {
  return (
    <button onClick={onClick} title={title}
      className={`p-2 rounded-md transition ring-1
        ${danger ? 'text-rose-600 bg-white ring-slate-200 hover:bg-rose-50 hover:ring-rose-200'
                 : 'text-slate-600 bg-white ring-slate-200 hover:bg-violet-50 hover:text-violet-700 hover:ring-violet-200'}`}>
      {children}
    </button>
  );
}

function LauncherView(props) {
  const { mode, bundleId, bundle, activeFile, setActiveFile, dirty, onContentChange,
          onSave, onReload, onChangeBundle, saving,
          form, setForm, onLaunch, modelLabel, accentClass } = props;
  return (
    <div className="space-y-6">
      {/* Bundle editor */}
      <div className="bg-white border rounded-2xl border-slate-200 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3 p-5 border-b border-slate-100">
          <div>
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
              <span>Bundle Files</span>
              <span className="px-2 py-0.5 text-[10px] tracking-wide rounded font-mono bg-violet-100 text-violet-700">{bundleId}</span>
            </div>
            <div className="mt-0.5 text-xs text-slate-500">
              {bundle ? `${bundle._id} · ${bundle.name || ''}` : 'Loading…'}
            </div>
          </div>
          <div className="flex gap-2">
            {onChangeBundle && (
              <Button size="sm" variant="outline" onClick={onChangeBundle} className="border-slate-200 text-slate-700 hover:bg-slate-100">
                <ChevronLeft className="w-4 h-4 mr-1" />Change bundle
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={onReload} className="border-slate-200 text-slate-700 hover:bg-slate-100">
              <RefreshCw className="w-4 h-4 mr-2" />Reload
            </Button>
            <Button size="sm" onClick={onSave} disabled={saving || !bundle || !dirty}
              title={!dirty ? 'No unsaved changes' : 'Save edited files to MongoDB'}
              className={`bg-gradient-to-r ${accentClass} text-white disabled:opacity-50 disabled:cursor-not-allowed`}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save to DB
            </Button>
          </div>
        </div>
        <div className="p-5 space-y-3">
          <div className="flex flex-wrap gap-2">
            {(bundle?.files || []).map((f, i) => (
              <button key={f.name} onClick={() => setActiveFile(i)}
                className={`px-3 py-1.5 text-xs font-semibold rounded transition ring-1
                  ${i === activeFile
                    ? 'bg-violet-600 text-white ring-violet-600'
                    : 'bg-white text-slate-600 ring-slate-200 hover:bg-slate-50'}`}>
                {f.name}
              </button>
            ))}
          </div>
          <textarea
            value={bundle?.files?.[activeFile]?.content || ''}
            onChange={(e) => onContentChange(e.target.value)}
            spellCheck={false}
            className="w-full h-80 px-3 py-3 font-mono text-xs leading-relaxed border rounded-lg border-slate-200 bg-slate-950 text-slate-100 focus:outline-none focus:ring-2 focus:ring-violet-500"
          />
          <div className="flex items-center justify-between text-xs text-slate-500">
            <span>{dirty && <span className="font-semibold text-amber-600">• Unsaved</span>} {bundle?.files?.[activeFile]?.name} · {(bundle?.files?.[activeFile]?.content || '').length} chars</span>
            <span>entrypoint: <code className="px-1 py-0.5 rounded bg-slate-100">{bundle?.entrypoint || (bundle?.files?.[0]?.name) || '—'}</code></span>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[360px_minmax(0,1fr)]">
        <div className="bg-white border rounded-2xl border-slate-200 shadow-sm">
          <div className="p-5 space-y-4">
            <div className="text-sm font-semibold text-slate-900">{mode === 'llm' ? 'LLM' : 'TTS'} Launch Config</div>

            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-slate-600">Instance Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-slate-600">GPU Type</Label>
              <select value={form.gpu} onChange={(e) => setForm({ ...form, gpu: e.target.value })}
                className="w-full px-3 py-2 text-sm border rounded-md border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-violet-500">
                {GPUS.map((g) => <option key={g} value={g}>{g}</option>)}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-slate-600">Storage (GB)</Label>
              <Input type="number" min="40" max="500" step="10" value={form.storage}
                onChange={(e) => setForm({ ...form, storage: parseInt(e.target.value) || 100 })} />
            </div>

            <Button onClick={onLaunch} className={`w-full bg-gradient-to-r ${accentClass} text-white`}>
              <Rocket className="w-4 h-4 mr-2" />
              Launch GPU + Deploy {mode === 'llm' ? 'Model' : 'TTS'}
            </Button>

            <div className="pt-2 mt-2 space-y-1.5 text-xs border-t border-slate-100">
              <Row k="Model" v={modelLabel} />
              <Row k="Exposed Port" v={bundle?.expose_port || '7860'} />
              <Row k="Entrypoint" v={bundle?.entrypoint || '—'} />
            </div>
          </div>
        </div>

        {/* Info card on the right (real-time log streaming happens on the Instances → 📜 LOGS flow) */}
        <div className="bg-white border rounded-2xl border-slate-200 shadow-sm">
          <div className="p-5">
            <div className="text-sm font-semibold text-slate-900">How this works</div>
            <ol className="mt-3 space-y-2 text-sm list-decimal list-inside text-slate-600">
              <li>Edit the setup script and supporting files above. Click <strong>Save to DB</strong> when ready.</li>
              <li>Set instance name + GPU + storage on the left, then hit <strong>Launch GPU + Deploy</strong>.</li>
              <li>The launcher creates a Jarvis VM, uploads every file in the bundle to <code className="px-1 py-0.5 rounded bg-slate-100">/home/&lt;name&gt;</code>, and runs the entrypoint.</li>
              <li>You'll be redirected to <strong>Instances</strong>. The new VM appears within ~30s.</li>
              <li>Click <strong>📜 Logs</strong> on the row to watch the live <code className="px-1 py-0.5 rounded bg-slate-100">sglang.log</code> and (for TTS) <code className="px-1 py-0.5 rounded bg-slate-100">api.log</code>.</li>
              <li>Pause via <strong>Pause</strong> action (storage kept, billing stops) or <strong>Destroy</strong> for full wipe.</li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  );
}

function BundlePicker({ category, bundles, onPick, onRefresh, accent }) {
  const isViolet = accent !== 'fuchsia';
  const ringHover = isViolet ? 'hover:border-violet-300 hover:bg-violet-50/50' : 'hover:border-fuchsia-300 hover:bg-fuchsia-50/50';
  const typeBadge = isViolet ? 'bg-violet-100 text-violet-700'                  : 'bg-fuchsia-100 text-fuchsia-700';
  const arrowClr  = isViolet ? 'text-violet-600'                                 : 'text-fuchsia-600';
  const heading   = category === 'llm' ? 'Pick an LLM bundle to launch' : 'Pick a TTS bundle to launch';

  return (
    <div className="bg-white border rounded-2xl border-slate-200 shadow-sm">
      <div className="flex items-center justify-between p-5 border-b border-slate-100">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">{heading}</h2>
          <p className="mt-0.5 text-sm text-slate-500">
            Each entry below is a separate bundle in <code className="px-1 py-0.5 rounded bg-slate-100">gpu_deploy</code> — pick which one to deploy.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={onRefresh}
          className="border-slate-200 text-slate-700 hover:bg-slate-100">
          <RefreshCw className="w-4 h-4 mr-2" />Refresh
        </Button>
      </div>

      <div className="p-5 space-y-3">
        {bundles === null ? (
          <div className="flex items-center justify-center gap-2 px-4 py-12 text-sm text-slate-500">
            <Loader2 className="w-4 h-4 animate-spin" />Loading bundles…
          </div>
        ) : bundles.length === 0 ? (
          <div className="px-4 py-12 text-sm text-center text-slate-500">
            No {category.toUpperCase()} bundles found.
          </div>
        ) : (
          bundles.map((b) => (
            <button key={b._id} onClick={() => onPick(b._id)} type="button"
              className={`w-full p-4 text-left border rounded-xl border-slate-200 transition ${ringHover}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="text-sm font-semibold text-slate-900 truncate">{b.name || '—'}</div>
                    <span className={`px-2 py-0.5 text-[10px] font-mono tracking-wide rounded ${typeBadge}`}>{b.type}</span>
                  </div>
                  <div className="text-xs font-mono text-slate-500">{b._id}</div>
                  <div className="flex flex-wrap gap-2 mt-2 text-xs text-slate-500">
                    <span>files: {(b.files || []).length}</span>
                    <span>•</span>
                    <span>entrypoint: <code className="px-1 rounded bg-slate-100">{b.entrypoint || '—'}</code></span>
                    <span>•</span>
                    <span>port: {b.expose_port || 7860}</span>
                    {b.updated_at && (
                      <>
                        <span>•</span>
                        <span>updated: {formatTimestamp(b.updated_at, user?.timezone)}</span>
                      </>
                    )}
                  </div>
                </div>
                <div className={`shrink-0 text-lg ${arrowClr}`}>→</div>
              </div>
            </button>
          ))
        )}
      </div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex justify-between"><span className="text-slate-500">{k}</span><span className="font-medium text-slate-700">{v}</span></div>
  );
}

function TerminalPanel({ logTab, setLogTab, mainLog, apiLog, dual, onClear }) {
  const lines = logTab === 'api' ? apiLog : mainLog;
  const scrollRef = useRef(null);
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [lines.length]);
  return (
    <div className="bg-slate-950 border rounded-2xl border-slate-200 shadow-sm overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2 bg-slate-900 border-b border-slate-800">
        <div className="flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-full bg-rose-500" />
          <span className="w-2.5 h-2.5 rounded-full bg-amber-500" />
          <span className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
        </div>
        {dual && (
          <div className="flex gap-1">
            <button onClick={() => setLogTab('main')}
              className={`px-3 py-1 text-xs font-semibold rounded ${logTab === 'main' ? 'bg-violet-600 text-white' : 'text-slate-300 hover:bg-slate-800'}`}>
              sglang.log
            </button>
            <button onClick={() => setLogTab('api')}
              className={`px-3 py-1 text-xs font-semibold rounded ${logTab === 'api' ? 'bg-violet-600 text-white' : 'text-slate-300 hover:bg-slate-800'}`}>
              api.log
            </button>
          </div>
        )}
        <button onClick={onClear} className="px-2 py-1 text-xs font-semibold rounded text-slate-300 hover:bg-slate-800">CLEAR</button>
      </div>
      <div ref={scrollRef} className="px-4 py-3 overflow-y-auto font-mono text-xs leading-relaxed" style={{ height: '60vh' }}>
        {lines.length === 0 ? (
          <div className="flex items-center justify-center h-full text-slate-500">Waiting for log lines…</div>
        ) : lines.map((l, i) => (
          <div key={i} className={`whitespace-pre-wrap break-all ${colorize(l)}`}>{l}</div>
        ))}
      </div>
    </div>
  );
}
